'use strict';

/**
 * État visible expédition — source unique Admin + Website (B2.4b3 final).
 * Shipment.status pilote le transport après préparation commerciale.
 */

const SHIPMENT_RANK = Object.freeze({
  DRAFT: 0,
  LABEL_CREATED: 10,
  SHIPPED: 20,
  IN_TRANSIT: 30,
  OUT_FOR_DELIVERY: 40,
  AVAILABLE_AT_PICKUP_POINT: 45,
  DELIVERED: 50,
  RETURN_IN_TRANSIT: 55,
  RETURNED: 60,
  FAILED: 60,
  CANCELLED: 60,
});

const TERMINAL_SHIPMENT = new Set(['DELIVERED', 'RETURNED', 'FAILED', 'CANCELLED']);

const CLIENT_LABELS = Object.freeze({
  DRAFT: 'En préparation',
  LABEL_CREATED: 'En attente de prise en charge',
  SHIPPED: 'Expédiée',
  IN_TRANSIT: 'En transit',
  OUT_FOR_DELIVERY: 'En cours de livraison',
  AVAILABLE_AT_PICKUP_POINT: 'Disponible en point relais',
  DELIVERED: 'Livrée',
  FAILED: 'Incident de livraison',
  RETURN_IN_TRANSIT: 'Retour en cours',
  RETURNED: 'Retournée',
  CANCELLED: 'Expédition annulée',
});

const MERCHANT_LABELS = Object.freeze({
  DRAFT: 'En préparation',
  LABEL_CREATED: 'En attente de prise en charge',
  SHIPPED: 'Expédiée',
  IN_TRANSIT: 'En transit',
  OUT_FOR_DELIVERY: 'En cours de livraison',
  AVAILABLE_AT_PICKUP_POINT: 'Disponible en point relais',
  DELIVERED: 'Livrée',
  FAILED: 'Incident transport',
  RETURN_IN_TRANSIT: 'Retour en cours',
  RETURNED: 'Retournée',
  CANCELLED: 'Expédition annulée',
});

const CLIENT_MESSAGES = Object.freeze({
  DRAFT: 'Votre commande est en cours de préparation pour l’expédition.',
  LABEL_CREATED:
    'Votre colis est prêt et attend sa prise en charge par le transporteur.',
  SHIPPED: 'Votre colis a été remis au transporteur.',
  IN_TRANSIT: 'Votre colis est en transit.',
  OUT_FOR_DELIVERY: 'Votre colis est en cours de livraison.',
  AVAILABLE_AT_PICKUP_POINT: 'Votre colis est disponible en point relais.',
  DELIVERED: 'Votre commande a été livrée.',
  FAILED: 'Un incident est survenu lors de la livraison. Le commerçant vous contactera si besoin.',
  RETURN_IN_TRANSIT: 'Votre colis est en cours de retour.',
  RETURNED: 'Votre colis a été retourné.',
  CANCELLED: 'L’expédition a été annulée.',
});

const ORDER_STATUT_RANK = Object.freeze({
  EN_ATTENTE: 0,
  CONFIRMEE: 1,
  EN_PREPARATION: 2,
  PRETE: 3,
  EN_LIVRAISON: 4,
  LIVREE: 5,
  RETIREE: 5,
  ANNULEE: 99,
  ANNULE: 99,
});

function normShipmentStatus(raw) {
  const key = String(raw || 'DRAFT').trim().toUpperCase();
  return SHIPMENT_RANK[key] != null ? key : 'DRAFT';
}

function shipmentRank(status) {
  return SHIPMENT_RANK[normShipmentStatus(status)] ?? -1;
}

function orderRank(statut) {
  const key = String(statut || 'EN_ATTENTE').trim().toUpperCase();
  return ORDER_STATUT_RANK[key] ?? -1;
}

function pickLabels(audience) {
  return audience === 'merchant' ? MERCHANT_LABELS : CLIENT_LABELS;
}

/** @returns {'HOME'|'RELAY'|null} */
function normalizeExpeditionDeliveryType(raw) {
  if (raw == null || raw === '') return null;
  const key = String(raw).trim().toUpperCase();
  if (key === 'HOME') return 'HOME';
  if (key === 'RELAY' || key === 'NON_HOME' || key === 'PICKUP_POINT' || key === 'PICKUP') {
    return 'RELAY';
  }
  return null;
}

function parseParcelSnapshotJson(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Type de livraison expédition (HOME vs relais) depuis données déjà persistées (devis).
 * @returns {'HOME'|'RELAY'|null}
 */
function resolveExpeditionDeliveryTypeFromCommandeLike(input) {
  if (!input || typeof input !== 'object') return null;
  const quote = input.shippingRateQuote || input.shippingQuote;
  let snap = parseParcelSnapshotJson(quote?.parcelSnapshotJson) || parseParcelSnapshotJson(input.parcelSnapshotJson);
  const raw =
    snap?.boxtalV1?.selectedOffer?.deliveryMode ??
    snap?.selectedOffer?.deliveryMode ??
    input.deliveryType ??
    input.expeditionDeliveryType ??
    null;
  return normalizeExpeditionDeliveryType(raw);
}

function shouldUseRelayTimelineLeg({ shipmentStatus, deliveryType }) {
  const ship = normShipmentStatus(shipmentStatus);
  const dt = normalizeExpeditionDeliveryType(deliveryType);
  if (ship === 'AVAILABLE_AT_PICKUP_POINT') return true;
  if (dt === 'RELAY' && ship === 'DELIVERED') return true;
  return false;
}

/**
 * @param {object} params
 * @param {string} [params.commandeStatus]
 * @param {string} [params.shipmentStatus]
 * @param {string|null} [params.carrier]
 * @param {string|null} [params.trackingNumber]
 * @param {string|null} [params.trackingUrl]
 * @param {'HOME'|'RELAY'|string|null} [params.deliveryType]
 * @param {'client'|'merchant'} [params.audience]
 */
function resolveExpeditionDisplayState(params = {}) {
  const audience = params.audience === 'merchant' ? 'merchant' : 'client';
  const labels = pickLabels(audience);
  const commandeStatus = String(params.commandeStatus || 'EN_ATTENTE').toUpperCase();
  const shipmentStatus = normShipmentStatus(params.shipmentStatus);
  const carrier = params.carrier ? String(params.carrier).trim() : null;
  const trackingNumber = params.trackingNumber ? String(params.trackingNumber).trim() : null;
  const trackingUrl = params.trackingUrl ? String(params.trackingUrl).trim() : null;
  const trackingAvailable = Boolean(trackingNumber || trackingUrl);

  const label = labels[shipmentStatus] || labels.DRAFT;
  const message = CLIENT_MESSAGES[shipmentStatus] || CLIENT_MESSAGES.DRAFT;
  const isFinal =
    TERMINAL_SHIPMENT.has(shipmentStatus) ||
    commandeStatus === 'LIVREE' ||
    commandeStatus === 'RETIREE';

  let pipelineBucket = 'en_cours';
  if (commandeStatus === 'ANNULEE' || commandeStatus === 'ANNULE') {
    pipelineBucket = 'annulee';
  } else if (
    shipmentStatus === 'DELIVERED' ||
    commandeStatus === 'LIVREE' ||
    commandeStatus === 'RETIREE'
  ) {
    pipelineBucket = 'terminee';
  } else if (shipmentStatus === 'CANCELLED') {
    pipelineBucket = 'annulee';
  }

  const deliveryType =
    params.deliveryType != null
      ? normalizeExpeditionDeliveryType(params.deliveryType)
      : null;

  const timelineSteps = buildExpeditionTimelineSteps({
    commandeStatus,
    shipmentStatus,
    audience,
    deliveryType,
  });
  const timelineStep = timelineSteps.find((s) => s.current)?.key || shipmentStatus;

  return {
    key: shipmentStatus,
    label,
    message: audience === 'merchant' ? label : message,
    isFinal,
    timelineStep,
    timelineSteps,
    pipelineBucket,
    carrier,
    trackingAvailable,
    trackingNumber,
    trackingUrl,
  };
}

function buildExpeditionTimelineSteps({ commandeStatus, shipmentStatus, audience, deliveryType = null }) {
  const ship = normShipmentStatus(shipmentStatus);
  const shipR = shipmentRank(ship);
  const ordR = orderRank(commandeStatus);
  const useRelayLeg = shouldUseRelayTimelineLeg({ shipmentStatus: ship, deliveryType });

  const clientSteps = [
    { key: 'RECEIVED', label: 'Commande reçue', minOrder: 0, minShip: -1 },
    { key: 'CONFIRMED', label: 'Confirmée', minOrder: 1, minShip: -1 },
    { key: 'PREPARATION', label: 'En préparation', minOrder: 2, minShip: 0 },
    {
      key: 'AWAITING_PICKUP',
      label: 'En attente de prise en charge',
      minOrder: 3,
      minShip: 10,
    },
    { key: 'SHIPPED', label: 'Expédiée', minOrder: 3, minShip: 20 },
    { key: 'IN_TRANSIT', label: 'En transit', minOrder: 3, minShip: 30 },
  ];

  if (useRelayLeg) {
    clientSteps.push({
      key: 'PICKUP_POINT',
      label: 'Disponible en point relais',
      minOrder: 3,
      minShip: 45,
    });
  } else {
    clientSteps.push({
      key: 'OUT_FOR_DELIVERY',
      label: 'En cours de livraison',
      minOrder: 3,
      minShip: 40,
    });
  }

  clientSteps.push({
    key: 'DELIVERED',
    label: audience === 'merchant' ? 'Livrée' : 'Livrée',
    minOrder: 3,
    minShip: 50,
  });

  let currentAssigned = false;
  return clientSteps.map((step) => {
    const orderOk = ordR >= step.minOrder;
    const shipOk = shipR >= step.minShip;
    const completed =
      step.key === 'DELIVERED'
        ? ship === 'DELIVERED' || commandeStatus === 'LIVREE'
        : orderOk && shipOk && (step.minShip < 0 || shipR >= step.minShip);

    let current = false;
    if (!currentAssigned && !completed && orderOk) {
      if (step.key === 'RECEIVED' && ordR === 0) current = true;
      else if (step.key === 'CONFIRMED' && ordR === 1) current = true;
      else if (step.key === 'PREPARATION' && ordR <= 2 && shipR < 10) current = true;
      else if (step.key === 'AWAITING_PICKUP' && ship === 'LABEL_CREATED') current = true;
      else if (step.key === 'SHIPPED' && ship === 'SHIPPED') current = true;
      else if (step.key === 'IN_TRANSIT' && ship === 'IN_TRANSIT') current = true;
      else if (step.key === 'OUT_FOR_DELIVERY' && ship === 'OUT_FOR_DELIVERY') current = true;
      else if (step.key === 'PICKUP_POINT' && ship === 'AVAILABLE_AT_PICKUP_POINT') current = true;
      else if (step.key === 'DELIVERED' && ship === 'DELIVERED') current = true;
      if (current) currentAssigned = true;
    }

    return {
      key: step.key,
      label: step.label,
      completed,
      current,
    };
  });
}

function resolveExpeditionListLabel(commande, audience = 'client') {
  if (!commande || String(commande.modeRetrait || '').toLowerCase() !== 'expedition') {
    return null;
  }
  const state = resolveExpeditionDisplayState({
    commandeStatus: commande.statut,
    shipmentStatus: commande.shipment?.status,
    carrier: commande.shipment?.carrier,
    trackingNumber: commande.shipment?.trackingNumber,
    trackingUrl: commande.shipment?.trackingUrl,
    deliveryType: resolveExpeditionDeliveryTypeFromCommandeLike(commande),
    audience,
  });
  return state.label;
}

function isExpeditionCommandeActive(commande) {
  if (!commande || String(commande.modeRetrait || '').toLowerCase() !== 'expedition') return null;
  const state = resolveExpeditionDisplayState({
    commandeStatus: commande.statut,
    shipmentStatus: commande.shipment?.status,
    deliveryType: resolveExpeditionDeliveryTypeFromCommandeLike(commande),
    audience: 'merchant',
  });
  if (state.pipelineBucket === 'annulee') return false;
  return state.pipelineBucket === 'en_cours';
}

function expeditionShipmentStatusLabelClient(status) {
  return CLIENT_LABELS[normShipmentStatus(status)] || CLIENT_LABELS.DRAFT;
}

function expeditionShipmentStatusLabelMerchant(status) {
  return MERCHANT_LABELS[normShipmentStatus(status)] || MERCHANT_LABELS.DRAFT;
}

export {
  SHIPMENT_RANK,
  normalizeExpeditionDeliveryType,
  resolveExpeditionDeliveryTypeFromCommandeLike,
  resolveExpeditionDisplayState,
  buildExpeditionTimelineSteps,
  resolveExpeditionListLabel,
  isExpeditionCommandeActive,
  expeditionShipmentStatusLabelClient,
  expeditionShipmentStatusLabelMerchant,
};
